import { el, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { keyManager } from "@dhx/ts-common/Keymanager";
import { IHandlers } from "@dhx/ts-common/types";

export enum EditorMode {
	editText = "text",
	selectItem = "select"
}

export enum EditorEvents {
	begin = "begin",
	end = "end"
}

export interface IEditorConfig {
	item?: any;
	mode: EditorMode;
	options?: string[];
}

export interface IEditor {
	config: IEditorConfig;
	events: IEventSystem<EditorEvents>;
	edit(targetId: string, config: IEditorConfig): VNode;
}

class Editor {
	public config: IEditorConfig;
	public events: IEventSystem<EditorEvents>;

	private _item: any;
	private _handlers: IHandlers;
	private _currentValue: string;
	private _active: boolean;
	private _targetId: string;
	private _documentClick: (e) => void;

	constructor() {
		this.events = new EventSystem();
		this._documentClick = e => {
			const id = locate(e, "id");
			if (id !== "input_" + this._item.id) {
				this._removeClickListener();
				this._finishEdit();
			}
		};
		this._handlers = {
			editText: (e) => {
				this._currentValue = e.target.value;
			},
			itemSelected: (e) => {
				this._currentValue = e.target.value;
				this._finishEdit();
			}
		};
	}
	edit(targetId: string, config: IEditorConfig) {
		if (this._active && this._item !== config.item) {
			this._finishEdit();
		}
		this._active = true;

		this.events.fire(EditorEvents.begin, [targetId]);
		this._targetId = targetId;
		this.config = config;
		this._item = config.item;
		this._currentValue = this._item.value;
		this._initOuterClick();

		this._addHotkeys();
		return this._draw();
	}
	private _draw() {
		if (this.config.mode === EditorMode.selectItem) {
			const opts = this.config.options;
			return el("select", {
				id: "input_" + this._item.id,
				dhx_id: this._item.id,
				onchange: this._handlers.itemSelected
			}, opts.map(item => el("option", {
					class: "editor-select",
					value: item,
					selected: this._currentValue === item,
					style: {border: "1px solid"}
				}, item))
			);
		} else {
			return el("input", {
				_hooks: {
					didInsert(newNode) {
						newNode.el.focus();
					}
				},
				id: "input_" + this._item.id,
				class: "dhx_tree-input",
				oninput: this._handlers.editText,
				value: this._item.value,
				autofocus: true
			});
		}
	}
	private _addHotkeys() {
		keyManager.addHotKey("escape", () => {
			this._finishEdit();
		}, this);
		keyManager.addHotKey("enter", () => {
			this._finishEdit();
		}, this);
	}
	private _removeHotkeys() {
		keyManager.removeHotKey(null, this);
	}
	private _finishEdit() {
		this.events.fire(EditorEvents.end, [this._targetId, this._item.id, this._currentValue]);
		this._clear();
	}
	private _clear() {
		this._active = false;
		this._removeClickListener();
		this._removeHotkeys();
	}
	private _initOuterClick() {
		document.addEventListener("click", this._documentClick);
	}
	private _removeClickListener() {
		document.removeEventListener("click", this._documentClick);
	}
}

export default new Editor();